'use client'

// /[locale]/forgot-password — запрос ссылки сброса.
//
// Экран «отправлено» показывается ВСЕГДА при 200, даже если такого адреса нет: бэкенд
// отвечает одинаково намеренно, и различать эти случаи в UI значило бы вернуть наружу
// ровно то, что он прячет — проверялку «есть ли у вас аккаунт».

import { useId, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { requestPasswordReset } from '@/shared/lib/auth'
import { logError } from '@/shared/lib/logError'
import {
    AuthShell, Field, SubmitButton, ErrorNote, useAuthErrorText,
} from '@/widgets/email-auth'

export default function ForgotPasswordPage() {
    const t = useTranslations('emailAuth')
    const tf = useTranslations('emailAuth.forgot')
    const errorText = useAuthErrorText()
    const errorId = useId()

    const [email, setEmail] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [sent, setSent] = useState(false)

    const submit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!email.trim()) {
            setError(t('errors.required'))
            return
        }
        setBusy(true)
        setError(null)
        try {
            await requestPasswordReset(email.trim())
            setSent(true)
        } catch (err) {
            // Сюда попадаем только на реальных сбоях (429, 5xx) — «адреса нет» бэкенд
            // ошибкой не считает.
            logError('requestPasswordReset', err)
            setError(errorText(err))
        } finally {
            setBusy(false)
        }
    }

    if (sent) {
        return (
            <AuthShell eyebrow={tf('eyebrow')} title={tf('sentTitle')} subtitle={tf('sentBody')}>
                <p className="t-meta">
                    <Link href="/login" className="link tap">
                        {tf('backToLogin')}
                    </Link>
                </p>
            </AuthShell>
        )
    }

    return (
        <AuthShell eyebrow={tf('eyebrow')} title={tf('title')} subtitle={tf('subtitle')}>
            <form onSubmit={submit}>
                <Field
                    id="forgot-email"
                    name="email"
                    label={t('labelEmail')}
                    type="email"
                    value={email}
                    onChange={setEmail}
                    placeholder={t('placeholderEmail')}
                    autoComplete="email"
                    autoFocus
                    invalid={!!error}
                    describedBy={errorId}
                />
                <ErrorNote id={errorId} message={error} />
                <SubmitButton busy={busy} label={tf('submit')} busyLabel={t('submitting')} />
            </form>

            <p className="t-meta mt-5">
                <Link href="/login" className="link tap">
                    {tf('backToLogin')}
                </Link>
            </p>
        </AuthShell>
    )
}
